// Type-safe query helpers — the only file that knows about table names.
// Everything downstream consumes typed objects from src/lib/types.ts.
//
// Why a thin facade and not direct sb.from() calls everywhere:
//   - One place to add caching / Etag-like reload coalescing later.
//   - One place to tweak field selection if a query gets slow.
//   - The viz functions become trivially testable with mocked `loadGraph()`.

import { sb } from "./db.ts";
import type {
  BusinessLine,
  Category,
  CronRun,
  CurrentRelevance,
  CurrentScore,
  InboxItem,
  Metric,
  ScoreMovement,
  Topic,
  TopicReadiness,
  TopicTrend,
  TopicWithContext,
  Trend,
} from "./types.ts";

/**
 * One-shot graph load: every category, trend, topic, the latest score per
 * (topic, metric) and the latest relevance per (topic, business_line). For
 * 189 topics × 11 metrics × 11 business lines this is ~5k rows. Postgres
 * returns it in tens of milliseconds; gzip-over-the-wire is a few hundred
 * kilobytes. Acceptable for v0.7; if it gets slow we'll page or denormalize.
 */
export async function loadGraph(): Promise<{
  categories: Category[];
  trends: Trend[];
  topics: Topic[];
  topicTrends: TopicTrend[];
  metrics: Metric[];
  businessLines: BusinessLine[];
  scoresByTopic: Record<number, Record<string, CurrentScore>>;
  relevanceByTopic: Record<number, Record<string, CurrentRelevance>>;
  readinessByTopic: Record<number, TopicReadiness>;
}> {
  const [
    cats,
    trends,
    topics,
    topicTrends,
    metrics,
    businessLines,
    scores,
    relevance,
    readiness,
  ] = await Promise.all([
    sb.from("categories").select("*").order("sort_order"),
    sb.from("trends").select("*").order("sort_order"),
    sb.from("topics").select("*").eq("is_archived", false).order("name"),
    sb.from("topic_trends").select("*"),
    sb.from("metrics").select("*").eq("is_active", true).order("sort_order"),
    sb.from("business_lines").select("*").eq("is_active", true).order("sort_order"),
    sb.from("topic_current_score").select("*"),
    sb.from("topic_current_relevance").select("*"),
    sb.from("topic_readiness").select("*"),
  ]);

  for (const r of [cats, trends, topics, topicTrends, metrics, businessLines, scores, relevance, readiness]) {
    if (r.error) throw new Error(r.error.message);
  }

  // Build keyed maps so callers don't need to scan arrays.
  const metricsById = new Map<number, Metric>(
    (metrics.data as Metric[]).map((m) => [m.id, m]),
  );
  const blsById = new Map<number, BusinessLine>(
    (businessLines.data as BusinessLine[]).map((b) => [b.id, b]),
  );

  const scoresByTopic: Record<number, Record<string, CurrentScore>> = {};
  for (const s of (scores.data as CurrentScore[]) || []) {
    const m = metricsById.get(s.metric_id);
    if (!m) continue;
    (scoresByTopic[s.topic_id] ||= {})[m.key] = s;
  }

  const relevanceByTopic: Record<number, Record<string, CurrentRelevance>> = {};
  for (const r of (relevance.data as CurrentRelevance[]) || []) {
    const bl = blsById.get(r.business_line_id);
    if (!bl) continue;
    (relevanceByTopic[r.topic_id] ||= {})[bl.key] = r;
  }

  const readinessByTopic: Record<number, TopicReadiness> = {};
  for (const r of (readiness.data as TopicReadiness[]) || []) {
    readinessByTopic[r.topic_id] = r;
  }

  return {
    categories: cats.data as Category[],
    trends: trends.data as Trend[],
    topics: topics.data as Topic[],
    topicTrends: topicTrends.data as TopicTrend[],
    metrics: metrics.data as Metric[],
    businessLines: businessLines.data as BusinessLine[],
    scoresByTopic,
    relevanceByTopic,
    readinessByTopic,
  };
}

/** Build the composite TopicWithContext view a caller wants for inspector / matrix bubble. */
export function composeTopic(
  topicId: number,
  graph: Awaited<ReturnType<typeof loadGraph>>,
): TopicWithContext | null {
  const topic = graph.topics.find((t) => t.id === topicId);
  if (!topic) return null;
  const memberships = graph.topicTrends
    .filter((tt) => tt.topic_id === topicId)
    .map((tt) => {
      const trend = graph.trends.find((t) => t.id === tt.trend_id)!;
      const category = graph.categories.find((c) => c.id === trend.category_id)!;
      return { trend, category, is_primary: tt.is_primary };
    });
  return {
    topic,
    memberships,
    scores: graph.scoresByTopic[topicId] ?? {},
    relevance: graph.relevanceByTopic[topicId] ?? {},
    readiness: graph.readinessByTopic[topicId] ?? null,
  };
}

export type PeriodScope = "W" | "M" | "Q" | "Y" | "ALL";

/** Convert a period scope into a date floor. ALL returns null (no floor). */
export function periodFloorISO(period: PeriodScope, now: Date = new Date()): string | null {
  if (period === "ALL") return null;
  const d = new Date(now);
  if (period === "W") d.setDate(d.getDate() - 7);
  else if (period === "M") d.setMonth(d.getMonth() - 1);
  else if (period === "Q") d.setMonth(d.getMonth() - 3);
  else if (period === "Y") d.setFullYear(d.getFullYear() - 1);
  return d.toISOString();
}

/**
 * Pending changes ranked for triage — feeds the inbox view. Filters by
 * created_at >= period floor when period != ALL, so the inbox shows only
 * recent activity at narrower scopes.
 */
export async function loadInbox(period: PeriodScope = "ALL"): Promise<InboxItem[]> {
  let q = sb.from("pending_inbox").select("*");
  const floor = periodFloorISO(period);
  if (floor) q = q.gte("created_at", floor);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data as InboxItem[]) ?? [];
}

/** Week-over-week deltas — feeds the "what moved this week" digest. */
export async function loadMovements(period: PeriodScope = "ALL"): Promise<ScoreMovement[]> {
  let q = sb.from("score_movements").select("*");
  const floor = periodFloorISO(period);
  if (floor) q = q.gte("week_start_iso", floor.slice(0, 10));
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data as ScoreMovement[]) ?? [];
}

/** Last 50 orchestrator invocations — feeds the Ops view. */
export async function loadRecentRuns(period: PeriodScope = "ALL"): Promise<CronRun[]> {
  let q = sb.from("recent_runs").select("*");
  const floor = periodFloorISO(period);
  if (floor) q = q.gte("started_at", floor);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data as CronRun[]) ?? [];
}
